/**
 * GenerateAssessmentPlan page — Triggers Assessment & Plan generation
 * without any recording or transcription controls.
 *
 * Embedded as an iframe from external applications. Receives dxList
 * and parameters from parent window, validates, constructs the
 * ScribeRequest payload, and sends it back to the parent.
 *
 * Supports mock mode via ?mock=true URL parameter for UI testing.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Types
import type { GenerateAssessmentPlanParams } from '@/types/generate-assessment-plan';

// Hooks
import { useGenerateAssessmentPlanInit } from '@/hooks/use-generate-assessment-plan-init';
import { useGenerateAssessmentPlanMessages } from '@/hooks/use-generate-assessment-plan-messages';
import { useGenerateAssessmentPlanPayload } from '@/hooks/use-generate-assessment-plan-payload';

// Components
import { GenerateAssessmentPlanStatus } from '@/components/GenerateAssessmentPlan/GenerateAssessmentPlanStatus';

// ============================================================================
// Mock mode component — simulated states, no API calls
// ============================================================================
const GenerateAssessmentPlanMock = () => {
  const [state, setState] = useState<
    'loading' | 'waiting' | 'generating'
  >('loading');

  useEffect(() => {
    // Simulate: loading → waiting → generating
    const timer1 = setTimeout(() => setState('waiting'), 1500);
    const timer2 = setTimeout(() => setState('generating'), 4000);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <GenerateAssessmentPlanStatus
      isValidating={state === 'loading'}
      isValidated={state !== 'loading'}
      scribeJobId={state !== 'loading' ? 'mock-job-id' : null}
      hasDxList={state === 'generating'}
      isGenerating={state === 'generating'}
      error={null}
    />
  );
};

// ============================================================================
// Real mode component — full integration
// ============================================================================
const GenerateAssessmentPlanReal = ({
  params,
}: {
  params: GenerateAssessmentPlanParams;
}) => {
  const {
    isValidated,
    isValidating,
    scribeJobId,
    gatewayToken,
    partnerId,
    error: initError,
  } = useGenerateAssessmentPlanInit(params);

  const { rawDxList, sendToParent } = useGenerateAssessmentPlanMessages({
    isValidated,
    isValidating,
    scribeJobId,
    error: initError,
  });

  const { isGenerating, error: payloadError } =
    useGenerateAssessmentPlanPayload({
      params,
      scribeJobId,
      rawDxList,
      partnerId: partnerId || '',
      gatewayToken,
      sendToParent,
    });

  // Combine errors
  const error = initError || payloadError;

  return (
    <GenerateAssessmentPlanStatus
      isValidating={isValidating}
      isValidated={isValidated}
      scribeJobId={scribeJobId}
      hasDxList={rawDxList !== null}
      isGenerating={isGenerating}
      error={error}
    />
  );
};

// ============================================================================
// Main entry point — routes to mock or real based on URL param
// ============================================================================
const GenerateAssessmentPlan = () => {
  const [searchParams] = useSearchParams();
  const isMockMode = searchParams.get('mock') === 'true';

  const params: GenerateAssessmentPlanParams = {
    appointmentId: searchParams.get('appointmentId') || '',
    patientId: searchParams.get('patientId') || '',
    locationId: searchParams.get('locationId') || '',
    integrationKey: searchParams.get('integrationKey') || '',
    partnerName: searchParams.get('partnerName') || '',
    patientCode: searchParams.get('patientCode') || '',
    clientPlatform: searchParams.get('clientPlatform') || undefined,
    patientFirstName: searchParams.get('patientFirstName') || undefined,
    patientLastName: searchParams.get('patientLastName') || undefined,
    patientDob: searchParams.get('patientDob') || undefined,
    debugMode: searchParams.get('debug') === 'true',
  };

  // Make background transparent for iframe embedding
  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    return () => {
      document.body.style.backgroundColor = prevBg;
      document.documentElement.style.backgroundColor = prevBg;
    };
  }, []);

  if (isMockMode) {
    return <GenerateAssessmentPlanMock />;
  }

  return <GenerateAssessmentPlanReal params={params} />;
};

export default GenerateAssessmentPlan;
